import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  SimpleGrid, Spinner, Alert, AlertIcon, Select, Divider,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiDollarSign } from 'react-icons/fi';
import api from '../api';

const fmt = (n) => '£' + parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_COLOUR = { draft: 'gray', processing: 'blue', completed: 'green', cancelled: 'red' };

export default function EmployeeTaxDashboard({ onBack }) {
  const [payRuns, setPayRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [taxYear, setTaxYear] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [runsRes, summaryRes] = await Promise.all([
        api.get('/salary/pay-runs'),
        api.get('/salary/employer-summary'),
      ]);
      setPayRuns(runsRes.data);
      setSummary(summaryRes.data);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openRun = async (run) => {
    setSelectedRun(run);
    setLoadingItems(true);
    try {
      const { data } = await api.get(`/salary/pay-runs/${run.id}/items`);
      setItems(data);
    } catch(e) { setItems([]); }
    setLoadingItems(false);
  };

  // Filter pay runs to tax year (Apr Y - Apr Y+1)
  const taxYearStart = `${taxYear}-04-06`;
  const taxYearEnd = `${taxYear + 1}-04-05`;
  const filteredRuns = payRuns.filter(r => {
    const d = r.pay_date?.split('T')[0];
    return d >= taxYearStart && d <= taxYearEnd;
  });

  // P32 summary: aggregate per run
  const p32Rows = filteredRuns.map(r => ({
    ...r,
    paye: parseFloat(r.total_tax || 0),
    ee_ni: parseFloat(r.total_ni || 0),
    er_ni: parseFloat(r.total_gross || 0) > 0
      ? parseFloat(r.total_gross || 0) * 0.138 // approximation for display
      : 0,
    total_due: parseFloat(r.total_tax || 0) + parseFloat(r.total_ni || 0),
  }));

  const p32Totals = p32Rows.reduce((a, r) => ({
    paye: a.paye + r.paye,
    ee_ni: a.ee_ni + r.ee_ni,
    er_ni: a.er_ni + r.er_ni,
    total_due: a.total_due + r.total_due,
  }), { paye: 0, ee_ni: 0, er_ni: 0, total_due: 0 });

  return (
    <Box p={4} maxW="1200px" mx="auto">
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onBack}>Back to Payroll</Button>
          <FiDollarSign size={18} />
          <Text fontWeight="700" fontSize="16px">Employee Tax (PAYE)</Text>
        </HStack>
        <HStack>
          <Text fontSize="11px" color="gray.500">Tax Year</Text>
          <Select size="xs" w="130px" value={taxYear} onChange={e => setTaxYear(parseInt(e.target.value))}>
            {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}/{y+1}</option>)}
          </Select>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={fetchAll}>Refresh</Button>
        </HStack>
      </Flex>

      {loading ? <Flex justify="center" py={8}><Spinner /></Flex> : (
        <>
          {/* Summary cards */}
          {summary && (
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
              {[
                ['Annual PAYE Liability', fmt(summary.annual?.paye_tax), 'red'],
                ['Annual Employee NI', fmt(summary.annual?.ee_ni), 'orange'],
                ['Annual Employer NI', fmt(summary.annual?.er_ni), 'orange'],
                ['Total Monthly HMRC Payment', fmt((summary.monthly?.paye_tax || 0) + (summary.monthly?.ee_ni || 0) + (summary.monthly?.er_ni || 0)), 'red'],
              ].map(([label, val, color]) => (
                <Box key={label} p={3} border="1px" borderColor="gray.200" borderRadius="lg">
                  <Text fontSize="9px" color="gray.500" textTransform="uppercase" fontWeight="600">{label}</Text>
                  <Text fontSize="15px" fontWeight="800" color={`${color}.600`}>{val}</Text>
                  <Text fontSize="9px" color="gray.400">all active employees</Text>
                </Box>
              ))}
            </SimpleGrid>
          )}

          <Alert status="info" mb={4} fontSize="11px" borderRadius="md">
            <AlertIcon />
            HMRC payments are due by the 19th (cheque) or 22nd (electronic) of the month following the tax month end.
            Tax year {taxYear}/{taxYear+1}: 6 Apr {taxYear} – 5 Apr {taxYear+1}.
          </Alert>

          {/* P32 Employer Payment Record */}
          <Text fontSize="12px" fontWeight="700" color="gray.700" mb={2}>
            P32 — Employer Payment Record: Tax Year {taxYear}/{taxYear+1}
          </Text>
          <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" mb={5}>
            <Table size="sm">
              <Thead bg="gray.800">
                <Tr>
                  <Th fontSize="9px" color="white">Pay Run</Th>
                  <Th fontSize="9px" color="white">Pay Date</Th>
                  <Th fontSize="9px" color="white" isNumeric>PAYE Tax</Th>
                  <Th fontSize="9px" color="white" isNumeric>Employee NI</Th>
                  <Th fontSize="9px" color="white" isNumeric>Total HMRC Due</Th>
                  <Th fontSize="9px" color="white">Status</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {filteredRuns.length === 0 && (
                  <Tr><Td colSpan={7} textAlign="center" py={6} color="gray.400" fontSize="12px">
                    No completed pay runs in tax year {taxYear}/{taxYear+1}
                  </Td></Tr>
                )}
                {p32Rows.map(r => (
                  <Tr key={r.id} cursor="pointer" _hover={{ bg: 'red.50' }}
                    bg={selectedRun?.id === r.id ? 'red.50' : undefined}
                    onClick={() => openRun(r)}>
                    <Td fontSize="12px" fontWeight="600" color="blue.600">{r.name}</Td>
                    <Td fontSize="11px">{r.pay_date ? new Date(r.pay_date+'T12:00:00').toLocaleDateString('en-GB') : '—'}</Td>
                    <Td isNumeric fontSize="11px" color="red.600">{fmt(r.paye)}</Td>
                    <Td isNumeric fontSize="11px" color="orange.600">{fmt(r.ee_ni)}</Td>
                    <Td isNumeric fontSize="12px" fontWeight="700" color="red.700">{fmt(r.total_due)}</Td>
                    <Td><Badge colorScheme={STATUS_COLOUR[r.status]} fontSize="8px" textTransform="capitalize">{r.status}</Badge></Td>
                    <Td><Button size="xs" variant="ghost" colorScheme="blue">Detail</Button></Td>
                  </Tr>
                ))}
                {filteredRuns.length > 0 && (
                  <Tr bg="gray.100" fontWeight="800">
                    <Td fontSize="11px" colSpan={2}>TOTALS</Td>
                    <Td isNumeric fontSize="11px" color="red.700">{fmt(p32Totals.paye)}</Td>
                    <Td isNumeric fontSize="11px" color="orange.700">{fmt(p32Totals.ee_ni)}</Td>
                    <Td isNumeric fontSize="12px" fontWeight="800" color="red.800">{fmt(p32Totals.total_due)}</Td>
                    <Td colSpan={2} />
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Pay run tax breakdown */}
          {selectedRun && (
            <Box>
              <Divider mb={3} />
              <Text fontSize="12px" fontWeight="700" color="gray.700" mb={2}>
                Tax Detail — {selectedRun.name}
              </Text>
              {loadingItems ? <Spinner size="sm" /> : (
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm">
                    <Thead bg="red.50">
                      <Tr>
                        <Th fontSize="9px">Employee</Th>
                        <Th fontSize="9px">Tax Code</Th>
                        <Th fontSize="9px" isNumeric>Gross</Th>
                        <Th fontSize="9px" isNumeric>PAYE Tax</Th>
                        <Th fontSize="9px" isNumeric>EE NI</Th>
                        <Th fontSize="9px" isNumeric>Total HMRC</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {items.map(item => (
                        <Tr key={item.id}>
                          <Td fontSize="12px" fontWeight="500">{item.employee_name}</Td>
                          <Td><Badge colorScheme="purple" fontSize="9px">{item.tax_code_used}</Badge></Td>
                          <Td isNumeric fontSize="11px">{fmt(item.gross_amount)}</Td>
                          <Td isNumeric fontSize="11px" color="red.600">{fmt(item.tax_amount)}</Td>
                          <Td isNumeric fontSize="11px" color="orange.600">{fmt(item.ni_amount)}</Td>
                          <Td isNumeric fontSize="12px" fontWeight="700" color="red.700">
                            {fmt(parseFloat(item.tax_amount||0) + parseFloat(item.ni_amount||0))}
                          </Td>
                        </Tr>
                      ))}
                      {items.length > 0 && (
                        <Tr bg="red.50" fontWeight="700">
                          <Td fontSize="11px" colSpan={3}>Total</Td>
                          <Td isNumeric fontSize="11px" color="red.700">{fmt(items.reduce((a,i) => a+parseFloat(i.tax_amount||0),0))}</Td>
                          <Td isNumeric fontSize="11px" color="orange.700">{fmt(items.reduce((a,i) => a+parseFloat(i.ni_amount||0),0))}</Td>
                          <Td isNumeric fontSize="12px" fontWeight="800" color="red.800">
                            {fmt(items.reduce((a,i) => a+parseFloat(i.tax_amount||0)+parseFloat(i.ni_amount||0),0))}
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
